import React, { useCallback } from "react";
import {
  Button,
  ContextMenu,
  ContextMenuPopup,
  ContextMenuTrigger,
  Icon,
  Menu,
  MenuPopup,
  MenuTrigger,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useCloseMenu,
} from "@salestrak/ui";
import { useIssueActions, type IssueActionRef } from "#app/hooks/kanban/use-issue-actions";
import { useDeleteIssueWithUndo } from "#app/hooks/kanban/use-delete-issue-with-undo";
import { useItemShareDialogActions } from "#app/lib/item-share-dialog-context";
import { useAutomationRunsDialogActions } from "#app/lib/automation-runs-dialog-context";
import { canManageSharing } from "#app/lib/utils/access-level";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import {
  CONTEXT_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import { useIssueRenameOptional } from "#app/components/issues/issue-rename-context";
import { useIssueViewOptional } from "#app/components/issue-view/use-issue-view";
import { renderIssueMenuItems } from "./issue-menu-items";
import type { Omittable } from "@salestrak/ui";

type IssueMenuProps = Omittable<{
  issue: IssueActionRef;
  /** "dropdown" (default) renders a three-dot button. "context" wraps children. */
  type?: "dropdown" | "context";
  /** The element to wrap when type="context". */
  children?: React.ReactNode;
  /** type="context" only: the wrapper stays, but nothing opens a menu. */
  disabled?: boolean;
  /** Extra menu items prepended before the standard ones. */
  prepend?: React.ReactNode;
  /** When true, show only the non-mutating items (Favorite + Copy
   *  link). For view / comment access tiers in the issue view. */
  readOnly?: boolean;
  /** Tooltip on the dropdown trigger — every visible ⋯ names itself (the
   *  detail-view header, the card cluster). */
  triggerTooltip?: string;
  /** Which side the trigger tooltip opens on. The detail-view header's ⋯
   *  sits under the dialog's top edge, so it reads down (the default); the
   *  card cluster's reads up, matching the Rename pencil beside it. */
  triggerTooltipSide?: "top" | "bottom";
  /** Trigger silhouette. Unset, the trigger keeps the Button's round default
   *  so it sits in the detail-view corner cluster at the same size and shape
   *  as the star, size-toggle and close buttons. Card title actions opt into
   *  the pill. */
  triggerShape?: React.ComponentProps<typeof Button>["shape"];
}>;

export type IssueMenuItemsProps = Omittable<{
  issue: IssueActionRef;
  /** Part set of the hosting menu kind. Omitted, the rows render as dropdown
   *  items. */
  parts?: MenuParts;
  /** Host-specific rows seated before the standard ones. */
  prepend?: React.ReactNode;
  readOnly?: boolean;
}>;

/**
 * THE wired project item roster: every handler the rows fire (rename,
 * delete-with-undo, share, automation activity) plus the pools behind the
 * stage / assignee / workflow / type submenus.
 *
 * It is a COMPONENT so the popup showing it owns its lifetime: a menu popup
 * mounts its children on open, so a closed menu holds no query subscriptions
 * and no mutation bindings. A row that keeps one of these mounted instead —
 * and there can be a hundred rows — pays for every project on screen.
 * `parts` comes from the host, so the same roster serves a ⋯ dropdown and its
 * right-click twin.
 */
export function IssueMenuItems({
  issue,
  parts,
  prepend,
  readOnly,
}: IssueMenuItemsProps): React.ReactNode {
  const actions = useIssueActions(issue);
  const closeMenu = useCloseMenu();
  const deleteWithUndo = useDeleteIssueWithUndo();
  const { openDialog: openShareDialog } = useItemShareDialogActions();
  const canShare = canManageSharing(issue.viewerAccessLevel);
  // Per-issue resolved gates — the backend already knows whether the
  // viewer's role + ownership + share state allow the action, so the
  // menu just reads the booleans. Default to false when missing so a
  // stale fragment can't briefly expose a row the user can't actually
  // act on.
  const canDelete = !!issue.viewerCanDelete;
  const canArchive = !!issue.viewerCanArchive;
  const handleShare = useCallback(() => {
    openShareDialog(issue.issueId);
  }, [openShareDialog, issue.issueId]);

  // "Automation activity" — same gating as the rest of automations: the
  // feature flag AND the manage permission (the dialog spans every automation
  // in the workspace, so it's an automations-admin surface). Only wired into
  // the menu when both hold, so the row (and its handler) stay absent
  // otherwise.
  const automationsEnabled = useAutomationsEnabled();
  const { canViewRuns } = useAutomationPermissions();
  const showAutomationRuns = automationsEnabled && canViewRuns;
  const { openDialog: openRunsDialog } = useAutomationRunsDialogActions();
  const handleAutomationRuns = useCallback(() => {
    openRunsDialog({ issueId: issue.issueId, issueName: issue.name }, "issue_menu");
  }, [openRunsDialog, issue.issueId, issue.name]);

  // Rename is INLINE wherever the hosting view provides a rename surface
  // (the board's card title); with no provider, the fallback opens the
  // project and focuses its title input — the title IS the rename editor.
  const rename = useIssueRenameOptional();
  const issueView = useIssueViewOptional();
  const handleRenameOpen = useCallback(() => {
    if (rename) {
      rename.onRenameStart(issue.issueId);
      return;
    }
    issueView?.openIssueById(issue.issueId, { focusTitle: true, source: "issue_menu" });
  }, [rename, issueView, issue.issueId]);

  const handleDelete = useCallback(() => {
    void deleteWithUndo(issue);
  }, [deleteWithUndo, issue]);

  return renderIssueMenuItems({
    ...actions,
    onPicked: closeMenu,
    parts,
    prepend,
    onAutomationRuns: showAutomationRuns ? handleAutomationRuns : undefined,
    onRename: handleRenameOpen,
    onDelete: handleDelete,
    onShare: handleShare,
    canManageSharing: canShare,
    canDelete,
    canArchive,
    readOnly,
  });
}

export function IssueMenu({
  issue,
  type = "dropdown",
  children,
  disabled,
  prepend,
  readOnly,
  triggerTooltip,
  triggerTooltipSide = "bottom",
  triggerShape,
}: IssueMenuProps) {
  const rosterProps = { issue, prepend, readOnly };

  const dropdownTrigger = (
    <MenuTrigger
      render={
        <Button
          aria-label="Project actions"
          variant="ghost"
          size="icon-sm"
          shape={triggerShape}
        />
      }
    >
      <Icon name="ellipsis" />
    </MenuTrigger>
  );

  return type === "context" ? (
    <ContextMenu disabled={disabled}>
      <ContextMenuTrigger>{children}</ContextMenuTrigger>
      <ContextMenuPopup width="action">
        <IssueMenuItems {...rosterProps} parts={CONTEXT_MENU_PARTS} />
      </ContextMenuPopup>
    </ContextMenu>
  ) : (
    <Menu>
      {triggerTooltip ? (
        <Tooltip>
          <TooltipTrigger render={dropdownTrigger} />
          <TooltipPopup side={triggerTooltipSide}>{triggerTooltip}</TooltipPopup>
        </Tooltip>
      ) : (
        dropdownTrigger
      )}
      <MenuPopup align="start" width="action">
        <IssueMenuItems {...rosterProps} />
      </MenuPopup>
    </Menu>
  );
}
